/**
 * Investigate Blackbaud read broker — the wire contract of
 * `GET /internal/investigate/blackbaud` (plan
 * docs/plans/2026-09-30-2103-feat-factory-investigate-run-plan.md, U3, KTD4).
 *
 * The broker is a GENERIC GET pass-through, not a catalog: any path under the
 * chosen product's base and any query parameter name are accepted. What it
 * narrows is what a call can DO — GET only, a persona bound to the school, a
 * token that never leaves the backend — not which reads the agent may make.
 *
 * Request: the five reserved query parameters below. Every OTHER query
 * parameter on the request is forwarded to SKY under its own name, value
 * re-encoded, so a SKY parameter cannot be named `runId`, `school`, `persona`,
 * `product` or `path`.
 *
 * Response (200): `{ verdict, status, body, capped }`. A mint failure, a SKY
 * 401 and an empty list are all 200s carrying their verdict — they are answers
 * about Blackbaud, not failures of the broker. A refusal (bad input, unsafe
 * path, unknown school or persona, persona from another school) is a 4xx with
 * the shared `{ error: { type, message } }` body and makes no SKY call.
 */

import { z } from 'zod'

/** SKY product → base path on `api.sky.blackbaud.com`, as sky-probe maps them. */
export const INVESTIGATE_BLACKBAUD_PRODUCT_BASES = {
  'school-core': '/school/v1',
  enrollment: '/afe-edems'
} as const
export const INVESTIGATE_BLACKBAUD_PRODUCTS = ['school-core', 'enrollment'] as const
export const InvestigateBlackbaudProductSchema = z.enum(INVESTIGATE_BLACKBAUD_PRODUCTS)
export type InvestigateBlackbaudProduct = z.infer<typeof InvestigateBlackbaudProductSchema>

/** sky-probe's verdict model; `cannot-mint` means no token could be obtained for the persona. */
export const INVESTIGATE_BLACKBAUD_VERDICTS = ['ok', 'empty', 'unauthorized', 'error', 'cannot-mint'] as const
export const InvestigateBlackbaudVerdictSchema = z.enum(INVESTIGATE_BLACKBAUD_VERDICTS)
export type InvestigateBlackbaudVerdict = z.infer<typeof InvestigateBlackbaudVerdictSchema>

/** Query parameter names the broker consumes; every other name is forwarded to SKY. */
export const INVESTIGATE_BLACKBAUD_RESERVED_PARAMS = ['runId', 'school', 'persona', 'product', 'path'] as const

/** Characters of SKY response text returned before the body is cut and `capped` is set. */
export const INVESTIGATE_BLACKBAUD_BODY_MAX_CHARS = 100_000

export const InvestigateBlackbaudReadQuerySchema = z.object({
  /** The investigate run this read belongs to; lands on the audit event. Same bounds as the run record. */
  runId: z.string().trim().min(1).max(200),
  /** School slug (a school public id also resolves). */
  school: z.string().trim().min(1).max(191),
  /** `admin` (the school's data-lookup connection) or a numeric `UsersOnSchools` id of that school. */
  persona: z.union([
    z.literal('admin'),
    z.string().regex(/^[1-9]\d{0,18}$/, 'persona is admin or a UsersOnSchools id')
  ]),
  product: InvestigateBlackbaudProductSchema,
  /** Path after the product base, e.g. `/users/123` or, for enrollment, `/v1/inquiries`. */
  path: z.string().min(1).max(2048)
})
export type InvestigateBlackbaudReadQuery = z.infer<typeof InvestigateBlackbaudReadQuerySchema>

export const InvestigateBlackbaudReadResponseSchema = z.object({
  verdict: InvestigateBlackbaudVerdictSchema,
  /** SKY's HTTP status; `0` when SKY was not reached (mint failure, transport error). */
  status: z.number().int().nonnegative(),
  /**
   * Parsed JSON when SKY returned JSON within the cap, raw text otherwise.
   * When `capped` is true this is the first `INVESTIGATE_BLACKBAUD_BODY_MAX_CHARS`
   * characters of the response text, which is no longer valid JSON.
   */
  body: z.unknown(),
  capped: z.boolean()
})
export type InvestigateBlackbaudReadResponse = z.infer<typeof InvestigateBlackbaudReadResponseSchema>
